// source-media/index.ts — public surface for the source-media bundle.
//
// Output is an IIFE (build-source-media-bundle.mjs) loaded as a footer <script>. It:
//   • attaches window.FtSourceMedia = { build, resolve } for ad-hoc callers, and
//   • defines + self-runs window.ftMountSourceMedia(root?), the placeholder scanner.
//
// Mount contract (mirrors the thumbnail mount, includes/js-src/thumbnail/index.ts): PHP's
// ft_source_media_mount() emits
//   <span class="ft-source-media-mount"><script type="application/json" class="ft-source-media-blob">{…}</script></span>
// The JSON-in-a-script survives innerHTML insertion. The scanner reads the blob, builds the cover/video
// node, and replaces the placeholder's children — idempotently.
//
// AJAX re-hydration is by EXPLICIT scoped re-invocation (ftMountSourceMedia(scope)) — deliberately NOT a
// body-wide MutationObserver (same rationale as the thumbnail bundle).

import { buildSourceMediaNode, resolveSourceMedia, type SourceMediaMountData } from './core';

declare global {
    interface Window {
        FtSourceMedia?: {
            build: (data: SourceMediaMountData) => HTMLElement;
            resolve: typeof resolveSourceMedia;
        };
        ftMountSourceMedia?: (root?: Element | Document) => void;
    }
}

// Scan `root` (default document) for un-mounted source-media placeholders, render each from its JSON blob.
function ftMountSourceMedia(root?: Element | Document): void {
    const scope: Element | Document = root || document;
    const mounts = scope.querySelectorAll('.ft-source-media-mount:not([data-source-media-mounted])');
    for (let i = 0; i < mounts.length; i++) {
        const el = mounts[i] as HTMLElement;
        el.setAttribute('data-source-media-mounted', '1'); // mark first so a thrown render never loops on rescan
        const blob = el.querySelector('script.ft-source-media-blob');
        if (!blob) continue;
        try {
            const data = JSON.parse(blob.textContent || 'null') as SourceMediaMountData | null;
            if (!data) continue;
            // Replace the whole placeholder span with the built <img>/<video> so it sits directly in the
            // media container (the cards/rail size the element via media_class, e.g. w-100 / __media-el) —
            // an inline wrapper span would otherwise collapse its width. Consuming the placeholder also
            // makes a rescan naturally idempotent (the .ft-source-media-mount node is gone).
            el.replaceWith(buildSourceMediaNode(data));
        } catch (e) {
            console.warn('ftMountSourceMedia: mount failed', e);
        }
    }
}

window.FtSourceMedia = { build: buildSourceMediaNode, resolve: resolveSourceMedia };
window.ftMountSourceMedia = ftMountSourceMedia;

// Render page-load mounts as soon as the bundle executes (footer <script>, so mounts above are parsed),
// and again on DOMContentLoaded for any that came after. The data-source-media-mounted flag makes the
// second pass a no-op on already-rendered placeholders.
if (document.readyState !== 'loading') {
    ftMountSourceMedia();
} else {
    document.addEventListener('DOMContentLoaded', () => ftMountSourceMedia());
}
